import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiCall } from "../main/Apicall";
import "../css/others/Card.css";
import Card from "../Reusablecomponents/Card";
import BackButton from "../Reusablecomponents/BackButton";
import ToastPopup from "../Popups/ToastPopup";
import CopyConfigPopup from "../Popups/CopyConfigPopup";
import { Client, createToastMessage, ToastMessage } from "../Interfaces/Interface";
const Clientdetails: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [originalClient, setOriginalClient] = useState<Client | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [showCopyConfigPopup, setShowCopyConfigPopup] = useState<boolean>(false);
  const [clientsList, setClientsList] = useState<Array<{ clientId: string, clientName: string, subdomain: string }>>([]);
  const [sourceSubdomain, setSourceSubdomain] = useState<string>("");
  const [targetSubdomain, setTargetSubdomain] = useState<string>("");
  const [showSameClientWarning, setShowSameClientWarning] = useState<boolean>(false);
  const [buttonVisibility, setButtonVisibility] = useState<{ 'copy-config': boolean; 'infra-config': boolean; 'db-config': boolean; 'test-connectivity-config': boolean; 'onboard-mail-config': boolean }>({ 'copy-config': false, 'infra-config': false, 'db-config': false, 'test-connectivity-config': false, 'onboard-mail-config': false });
  const editableFields = ["clientName", "primarySpocName", "primarySpocEmail", "secondarySpocName", "secondarySpocEmail"];
  const iconMapper: { [key: string]: string } = { clientId: "fas fa-id-badge", clientName: "fas fa-user", subdomain: "fas fa-globe", brandingConfig: "fas fa-paint-brush", createdBy: "fas fa-user", createdDate: "fas fa-calendar-alt", primarySpocName: "fas fa-user-shield", primarySpocEmail: "fas fa-envelope", secondarySpocName: "fas fa-user-shield", secondarySpocEmail: "fas fa-envelope" };
  const fetchButtonVisibility = useCallback(async () => {
    try {
      const data = await apiCall("get", `/api/buttons/type-visible`);
      if (data && Array.isArray(data)) {
        const visibilityState = { 'copy-config': false, 'infra-config': false, 'db-config': false, 'test-connectivity-config': false, 'onboard-mail-config': false };
        data.forEach((item: { type: string, visible: boolean }) => {
          if (item.type in visibilityState) {
            visibilityState[item.type as keyof typeof visibilityState] = item.visible;
          }
        });
        setButtonVisibility(visibilityState);
      }
    } catch (error) {
      console.error("Failed to fetch button visibility:", error);
    }
  }, []);
  const fetchAllClients = useCallback(async () => {
    try {
      const data = await apiCall("get", `/api/clients/all?page=0&size=100`);
      if (data && data.content) {
        const formattedClients = data.content.map((client: any) => ({ clientId: client.clientId, clientName: client.clientName, subdomain: client.subdomain }));
        setClientsList(formattedClients);
      }
    } catch (error) {
      console.error("Failed to fetch clients list:", error);
    }
  }, []);
  useEffect(() => {
    fetchButtonVisibility();
    fetchAllClients();
  }, [fetchAllClients, fetchButtonVisibility]);
  const fetchClientDetails = useCallback(async () => {
    if (!clientId) return;
    try {
      const clientData = await apiCall("get", `/api/clients`, null, { clientId });
      const { updatedBy, updatedDate, ...cleanData } = clientData;
      setClient(cleanData);
      setOriginalClient(cleanData);
      setTargetSubdomain(cleanData.subdomain)
    } catch (error) {
      setError("Failed to load client details.");
    }
  }, [clientId]);
  useEffect(() => {
    fetchClientDetails();
  }, [fetchClientDetails]);
  const handleSave = async () => {
    if (!client) return;
    for (const field of editableFields) {
      if (!client[field]) {
        setMessage(createToastMessage("All fields are required.", "warning", 3000));
        return;
      }
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(client.primarySpocEmail!) || !emailRegex.test(client.secondarySpocEmail!)) {
      setMessage(createToastMessage("Invalid email format.", "warning", 3000));
      return;
    }
    try {
      const filteredPayload: Client = {};
      editableFields.forEach((key) => { filteredPayload[key] = client[key] });
      const payload = { ...filteredPayload, updatedBy: "admin" };
      const response = await apiCall("put", `/api/clients`, payload, { clientId });
      setClient(response);
      setOriginalClient(response);
      setIsEditing(false);
      setMessage(createToastMessage("Client data saved successfully!", "success", 3000));
      setTimeout(() => window.location.reload(), 3000);
    } catch (error) {
      setMessage(createToastMessage("Failed to update client.", "error", 3000));
    }
  };
  const handleConnectivityTest = async () => {
    if (!clientId) return;
    try {
      const result = await apiCall("get", `/api/clients/${clientId}/test-connectivity`);
      setMessage(createToastMessage(result, "success", 3000));
    } catch (error) {
      setMessage(createToastMessage("Connectivity test failed!", "error", 3000));
    }
  };
  const handleCreateDb = async () => {
    if (!clientId || !client?.subdomain) return;
    if (client.isDBCreated) {
      setMessage(createToastMessage("Database has already been created!", "warning", 3000));
      return;
    }
    try {
      const result = await apiCall("post", `/api/clients/${clientId}/database?subdomain=${client.subdomain}`);
      setMessage(createToastMessage(result, "success", 3000));
    } catch (error) {
      setMessage(createToastMessage("Database creation failed!", "error", 3000));
    }
  };
  const handleSendInvite = async () => {
    if (!clientId) return;
    try {
      const result = await apiCall("post", `/api/clients/${clientId}/send-onboarding-email`);
      setMessage(createToastMessage(result || "Onboarding email sent successfully!", "success", 3000));
    } catch (error) {
      setMessage(createToastMessage("Failed to send onboarding email!", "error", 3000));
    }
  };
  const handleCopyConfig = () => {
    if (client?.isBaselineCopied) {
      setMessage(createToastMessage("Baseline configuration has already been copied!", "warning", 3000));
      return;
    }
    setShowCopyConfigPopup(true);
    setSourceSubdomain("");
    setShowSameClientWarning(false);
  };
  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSourceSubdomain = e.target.value;
    setSourceSubdomain(newSourceSubdomain);
    if (newSourceSubdomain && targetSubdomain && newSourceSubdomain === targetSubdomain) {
      setShowSameClientWarning(true);
    } else {
      setShowSameClientWarning(false);
    }
  };
  const handleConfirmCopyConfig = async () => {
    if (!sourceSubdomain || !targetSubdomain) {
      setMessage(createToastMessage("Please select both source and target clients.", "warning", 3000));
      return;
    }
    if (sourceSubdomain === targetSubdomain) {
      setMessage(createToastMessage("Source and target clients cannot be the same.", "warning", 3000));
      return;
    }
    try {
      await apiCall("post", `/api/clients/config/copy?sourceSubdomain=${sourceSubdomain}&targetSubdomain=${targetSubdomain}`);
      setMessage(createToastMessage("Config copied successfully!", "success", 2000));
      setShowCopyConfigPopup(false);
      setTimeout(() => window.location.reload(), 3000);
    } catch (error) {
      setMessage(createToastMessage("Copy Config failed!", "error", 3000));
    }
  };
  const handleGenerateFile = async () => {
    if (!clientId) return;
    if (client?.isInfraCreated) {
      setMessage(createToastMessage("Infrastructure files have already been created!", "warning", 3000));
      return;
    }
    try {
      const result = await apiCall("post", `/api/clients/${clientId}/generate`);
      setMessage(createToastMessage(result, "success", 2000));
      setTimeout(() => window.location.reload(), 3000);
    } catch (error) {
      setMessage(createToastMessage("Infra File Creation failed!", "error", 3000));
    }
  };
  const handleCancel = () => {
    setClient(originalClient);
    setIsEditing(false);
    setError(null);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setClient((prevClient) => (prevClient ? { ...prevClient, [name]: value } : null));
  };
  if (error || !client) return null;
  return (
    <div className="container">
      <div className="inner-container">
        <h1 className="titlee">Client Details</h1>
        <div className="button-group">
          {buttonVisibility['db-config'] && (
            <button className="copydb" onClick={handleCreateDb}>Create DB</button>
          )}
          {client.isDBCreated && buttonVisibility['test-connectivity-config'] && (
            <button className="connectivity" onClick={handleConnectivityTest}>Test Connectivity</button>
          )}
          {client.isDBCreated && buttonVisibility['copy-config'] && (
            <button className="copyconfig" onClick={handleCopyConfig}>Copy Config</button>
          )}
          {client.isBaselineCopied && buttonVisibility['infra-config'] && (
            <button className="clientinfra" onClick={handleGenerateFile}>Infra Files</button>
          )}
          {client.isProductionReady && buttonVisibility[`onboard-mail-config`] && (
            <button className="send-invite" onClick={handleSendInvite}>Send Onboarding Email</button>
          )}
        </div>
      </div>
      <div className="card-container">
        <div className="grid">
          {Object.entries(client).map(([key, value]) => {
            if (["updatedBy", "updatedDate", "createdBy", "createdDate", "isDBCreated", "isInfraCreated", "isBaselineCopied", "isProductionReady"].includes(key)) return null;
            const iconClass = iconMapper[key] || "fas fa-question";
            const label = key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase());
            const displayValue = value ?? "N/A";
            return (
              <Card key={key} label={label} value={displayValue} iconClass={iconClass} isEditing={isEditing && editableFields.includes(key)} name={key} onChange={handleChange} />
            );
          })}
        </div>
      </div>
      <nav className="buttons">
        <BackButton className="back-button" label="Back" />
        {isEditing ? (
          <>
            <button className="submit-button" onClick={handleSave}>Save</button>
            <button className="cancel-button" onClick={handleCancel}>Cancel</button>
          </>
        ) : (
          <button className="edit-button" onClick={() => setIsEditing(true)}>Edit</button>
        )}
      </nav>
      <CopyConfigPopup
        isOpen={showCopyConfigPopup}
        onClose={() => setShowCopyConfigPopup(false)}
        onConfirm={handleConfirmCopyConfig}
        sourceSubdomain={sourceSubdomain}
        targetSubdomain={targetSubdomain}
        onSourceChange={handleSourceChange}
        clientsList={clientsList}
        showSameClientWarning={showSameClientWarning}
        isConfirmDisabled={!sourceSubdomain || showSameClientWarning}
      />
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default Clientdetails;